import { useState, useEffect, useCallback, useRef } from "react";
import { Loader2, Paperclip, Send, Search, FileText, X, Inbox as InboxIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ConfirmDelete } from "@/components/admin/ConfirmDelete";

const B = process.env.REACT_APP_BACKEND_URL;
const when = (iso) => { const d = new Date(iso); return d.toDateString() === new Date().toDateString() ? d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : d.toLocaleDateString(); };

const Bubble = ({ m, mine, isAdmin, onDeleted }) => (
  <div className={`flex items-end gap-1 group ${mine ? "justify-end" : "justify-start"}`} data-testid={`inbox-msg-${m.id}`}>
    {isAdmin && mine && <span className="opacity-0 group-hover:opacity-100"><ConfirmDelete url={`/api/messages/${m.id}`} title="Delete this message?" description="It disappears for both sides." onDone={onDeleted} testId={`inbox-msg-delete-${m.id}`} /></span>}
    <div className={`max-w-[80%] rounded-2xl px-4 py-2 text-sm shadow-sm ${mine ? "bg-blue-600 text-white rounded-br-sm" : "bg-white border border-slate-200 text-slate-800 rounded-bl-sm"}`}>
      {m.content && <p className="whitespace-pre-wrap break-words">{m.content}</p>}
      {(m.attachments || []).map((a, i) => (
        a.content_type?.startsWith("image/") ? <a key={i} href={`${B}${a.url}`} target="_blank" rel="noreferrer"><img src={`${B}${a.url}`} alt={a.name} className="mt-2 max-h-48 rounded-lg" /></a>
          : <a key={i} href={`${B}${a.url}`} target="_blank" rel="noreferrer" className={`mt-2 flex items-center gap-1 text-xs underline ${mine ? "text-blue-100" : "text-blue-700"}`}><FileText className="w-3.5 h-3.5" />{a.name}</a>
      ))}
      <p className={`text-[10px] mt-1 ${mine ? "text-blue-200" : "text-slate-400"}`}>{m.from_team && m.sender_name ? `${m.sender_name} · ` : ""}{when(m.created_at)}</p>
    </div>
    {isAdmin && !mine && <span className="opacity-0 group-hover:opacity-100"><ConfirmDelete url={`/api/messages/${m.id}`} title="Delete this message?" description="It disappears for both sides." onDone={onDeleted} testId={`inbox-msg-delete-${m.id}`} /></span>}
  </div>
);

const Composer = ({ threadId, onSent }) => {
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const list = [...e.target.files].slice(0, 5 - files.length);
    e.target.value = "";
    for (const f of list) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} is over 10MB`); continue; }
      const fd = new FormData(); fd.append("file", f);
      try { const { data } = await api.post("/api/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); setFiles(x => [...x, { url: data.url, name: f.name, content_type: f.type }]); }
      catch { toast.error(`Couldn't upload ${f.name}`); }
    }
  };
  const send = async () => {
    if (!text.trim() && !files.length) return;
    setBusy(true);
    try { await api.post(`/api/inbox/threads/${threadId}`, { content: text, attachments: files, origin_url: window.location.origin }); setText(""); setFiles([]); onSent(); }
    catch (e) { toast.error(e.response?.data?.detail || "Message failed"); } finally { setBusy(false); }
  };
  return (
    <div className="border-t border-slate-200 p-3 bg-white" data-testid="inbox-composer">
      {files.length > 0 && <div className="flex flex-wrap gap-2 mb-2">{files.map((f, i) => <span key={i} className="text-xs bg-slate-100 rounded-full px-2 py-1 flex items-center gap-1">{f.name}<button onClick={() => setFiles(x => x.filter((_, j) => j !== i))}><X className="w-3 h-3" /></button></span>)}</div>}
      <div className="flex items-end gap-2">
        <label className="p-2 rounded-lg hover:bg-slate-100 cursor-pointer text-slate-500" title="Attach photos or PDFs" data-testid="inbox-attach"><Paperclip className="w-5 h-5" /><input type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={pick} data-testid="inbox-attach-input" /></label>
        <textarea value={text} onChange={e => setText(e.target.value)} onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} rows={1} placeholder="Write a message…" className="flex-1 resize-none border border-slate-300 rounded-xl px-3 py-2 text-sm max-h-32 focus:outline-none focus:ring-2 focus:ring-blue-500" data-testid="inbox-input" />
        <button onClick={send} disabled={busy} className="bg-blue-600 hover:bg-blue-500 text-white p-2.5 rounded-xl disabled:opacity-50" data-testid="inbox-send">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}</button>
      </div>
    </div>
  );
};

const Thread = ({ threadId, title, isAdmin, onRead }) => {
  const [msgs, setMsgs] = useState(null);
  const end = useRef(null);
  const load = useCallback(() => api.get(`/api/inbox/threads/${threadId}`).then(r => { setMsgs(r.data); onRead?.(); }).catch(() => setMsgs([])), [threadId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { setMsgs(null); load(); const t = setInterval(load, 8000); return () => clearInterval(t); }, [load]);
  useEffect(() => { end.current?.scrollIntoView({ block: "end" }); }, [msgs?.length]);
  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="px-4 py-3 border-b border-slate-200 bg-white font-semibold text-slate-900 flex items-center justify-between gap-2" data-testid="inbox-thread-title">
        <span className="truncate">{title}</span>
        {isAdmin && <ConfirmDelete url={`/api/messages/conversation/${threadId}`} label="Conversation" title="Delete the whole conversation?" description="Deletes every message in this thread." onDone={load} testId="inbox-thread-delete" />}
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="inbox-messages">
        {!msgs ? <Loader2 className="w-5 h-5 animate-spin text-slate-400 mx-auto" /> : msgs.length === 0 ? <p className="text-sm text-slate-400 text-center py-10">No messages yet — say hello.</p>
          : msgs.map(m => <Bubble key={m.id} m={m} mine={isAdmin ? m.from_team : !m.from_team} isAdmin={isAdmin} onDeleted={load} />)}
        <div ref={end} />
      </div>
      <Composer threadId={threadId} onSent={load} />
    </div>
  );
};

export const Inbox = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [threads, setThreads] = useState([]);
  const [active, setActive] = useState(null);
  const [q, setQ] = useState("");
  const [customers, setCustomers] = useState([]);
  const load = useCallback(() => api.get("/api/inbox/threads", { params: { q } }).then(r => setThreads(r.data)).catch(() => {}), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { if (isAdmin) api.get("/api/admin/users").then(r => setCustomers(r.data.filter(u => u.role !== "admin"))).catch(() => {}); }, [isAdmin]);

  if (!isAdmin) return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden h-[70vh] min-h-[420px]" data-testid="inbox">
      {user && <Thread threadId={user.id || user._id} title="ShopAdvisor Team" isAdmin={false} />}
    </div>
  );
  const current = threads.find(t => t.user_id === active) || (active && customers.find(c => c.id === active) && { name: customers.find(c => c.id === active).name, email: customers.find(c => c.id === active).email });
  return (
    <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden h-[75vh] min-h-[460px] flex" data-testid="inbox">
      <div className={`${active ? "hidden md:flex" : "flex"} w-full md:w-80 shrink-0 flex-col border-r border-slate-200`}>
        <div className="p-3 border-b border-slate-200 space-y-2">
          <div className="relative"><Search className="w-4 h-4 absolute left-2.5 top-2.5 text-slate-400" /><Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search messages or people" className="pl-8" data-testid="inbox-search" /></div>
          <select value="" onChange={e => e.target.value && setActive(e.target.value)} className="w-full h-9 rounded-md border border-slate-300 bg-white px-2 text-sm" data-testid="inbox-new-select">
            <option value="">+ New message to…</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.name} ({c.email})</option>)}
          </select>
        </div>
        <div className="flex-1 overflow-y-auto">
          {threads.length === 0 && <p className="text-sm text-slate-400 text-center py-10 flex flex-col items-center gap-2"><InboxIcon className="w-6 h-6" />{q ? "No matches" : "Inbox is empty"}</p>}
          {threads.map(t => (
            <button key={t.user_id} onClick={() => setActive(t.user_id)} className={`w-full text-left px-3 py-3 border-b border-slate-100 hover:bg-slate-50 ${active === t.user_id ? "bg-blue-50" : ""}`} data-testid={`inbox-thread-${t.user_id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className={`truncate text-sm ${t.unread ? "font-bold text-slate-900" : "font-medium text-slate-700"}`}>{t.name}</span>
                <span className="text-[10px] text-slate-400 shrink-0">{t.last && when(t.last.created_at)}</span>
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className={`truncate text-xs ${t.unread ? "text-slate-800" : "text-slate-500"}`}>{t.last?.from_team ? "You: " : ""}{t.last?.content || (t.last?.attachments?.length ? "Attachment" : "")}</span>
                {t.unread > 0 && <span className="shrink-0 bg-blue-600 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5" data-testid={`inbox-unread-${t.user_id}`}>{t.unread}</span>}
              </div>
            </button>
          ))}
        </div>
      </div>
      <div className={`${active ? "flex" : "hidden md:flex"} flex-1 min-w-0 flex-col`}>
        {active ? (
          <>
            <button onClick={() => setActive(null)} className="md:hidden text-sm text-blue-600 px-4 pt-2 text-left" data-testid="inbox-back">← All conversations</button>
            <Thread threadId={active} title={current ? `${current.name} · ${current.email}` : "Conversation"} isAdmin onRead={load} />
          </>
        ) : <div className="flex-1 flex items-center justify-center text-sm text-slate-400">Pick a conversation</div>}
      </div>
    </div>
  );
};
